/**
 * Entry point for the sync layer.
 *
 * Call `startSupabaseSync(userId)` exactly once after the user is
 * authenticated. It will:
 *   1. Hydrate Zustand stores (workspace + activity) from the server.
 *   2. Subscribe to table changes via Supabase Realtime.
 *   3. Attach a Zustand subscriber that pushes local mutations back.
 *
 * Call `stopSupabaseSync()` on sign-out.
 */
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { resyncAutomationBaseline } from '@/lib/automationRunner';
import {
  rowToWorkspace, rowToBoard, rowToGroup, rowToColumn, rowToItem, rowToView,
  workspaceToRow, boardToRow, groupToRow, columnToRow, itemToRow, viewToRow,
} from './mapping';

let started = false;
let userId: string | null = null;
let channels: { unsubscribe: () => void }[] = [];
let unsubscribeStore: (() => void) | null = null;

/** Replace in-memory state with server snapshots for the authed user. */
async function hydrate() {
  if (!supabase) return;

  const [{ data: workspaces }, { data: boards }, { data: groups },
         { data: columns },   { data: items },  { data: views  }] =
    await Promise.all([
      supabase.from('workspaces').select('*'),
      supabase.from('boards').select('*'),
      supabase.from('groups').select('*'),
      supabase.from('columns').select('*'),
      supabase.from('items').select('*'),
      supabase.from('board_views').select('*'),
    ]);

  useWorkspaceStore.setState({
    workspaces: (workspaces ?? []).map(rowToWorkspace),
    boards:     (boards ?? []).map(rowToBoard),
    groups:     (groups ?? []).map(rowToGroup),
    columns:    (columns ?? []).map(rowToColumn),
    items:      (items ?? []).map(rowToItem),
    views:      (views ?? []).map(rowToView),
    activeWorkspaceId: workspaces?.[0]?.id ?? '',
  });
}

/** Push a single row change to Supabase. Fire-and-forget; realtime will reconcile. */
function push(
  table: string,
  row: Record<string, unknown>,
  op: 'upsert' | 'delete',
) {
  if (!supabase || !userId) return;
  if (op === 'upsert') {
    void supabase.from(table).upsert(row).then(({ error }) => {
      if (error) console.warn(`[sync] ${table} upsert failed:`, error.message);
    });
  } else {
    void supabase.from(table).delete().eq('id', row.id).then(({ error }) => {
      if (error) console.warn(`[sync] ${table} delete failed:`, error.message);
    });
  }
}

/** Compare two arrays of id'd records and emit upserts/deletes. */
function diff<T extends { id: string }>(
  prev: T[], next: T[], toRow: (r: T) => Record<string, unknown>, table: string,
) {
  const prevById = new Map(prev.map((r) => [r.id, r]));
  const nextById = new Map(next.map((r) => [r.id, r]));

  // Added or changed
  for (const [id, row] of nextById) {
    const p = prevById.get(id);
    if (!p || JSON.stringify(p) !== JSON.stringify(row)) {
      push(table, toRow(row), 'upsert');
    }
  }
  // Deleted
  for (const [id, row] of prevById) {
    if (!nextById.has(id)) push(table, toRow(row), 'delete');
  }
}

/** Subscribe to server changes and fold them into Zustand. */
function attachRealtime() {
  if (!supabase) return;

  function wire(table: string, onChange: (payload: any) => void) {
    const ch = supabase!.channel(`myday:${table}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, onChange)
      .subscribe();
    channels.push({ unsubscribe: () => { void ch.unsubscribe(); } });
  }

  wire('workspaces', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.workspaces];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToWorkspace(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { workspaces: list };
    });
  });

  wire('boards', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.boards];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToBoard(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { boards: list };
    });
  });

  wire('groups', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.groups];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToGroup(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { groups: list };
    });
  });

  wire('columns', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.columns];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToColumn(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { columns: list };
    });
  });

  wire('items', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.items];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToItem(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { items: list };
    });
  });

  wire('board_views', (p) => {
    useWorkspaceStore.setState((s) => {
      const list = [...s.views];
      const idx = list.findIndex((x) => x.id === p.new?.id || x.id === p.old?.id);
      if (p.eventType === 'DELETE') {
        if (idx >= 0) list.splice(idx, 1);
      } else if (p.new) {
        const mapped = rowToView(p.new);
        if (idx >= 0) list[idx] = mapped; else list.push(mapped);
      }
      return { views: list };
    });
  });
}

/** Attach a Zustand subscriber that pushes local mutations to Supabase. */
function attachPusher() {
  let previous = useWorkspaceStore.getState();

  unsubscribeStore = useWorkspaceStore.subscribe((next) => {
    if (!userId) return;

    if (next.workspaces !== previous.workspaces) {
      diff(previous.workspaces, next.workspaces, (w) => workspaceToRow(w, userId!), 'workspaces');
    }
    if (next.boards !== previous.boards) {
      diff(previous.boards, next.boards, boardToRow, 'boards');
    }
    if (next.groups !== previous.groups) {
      diff(previous.groups, next.groups, groupToRow, 'groups');
    }
    if (next.columns !== previous.columns) {
      diff(previous.columns, next.columns, columnToRow, 'columns');
    }
    if (next.items !== previous.items) {
      diff(previous.items, next.items, (it) => itemToRow(it, userId!), 'items');
    }
    if (next.views !== previous.views) {
      diff(previous.views, next.views, viewToRow, 'board_views');
    }

    previous = next;
  });
}

export async function startSupabaseSync(uid: string): Promise<boolean> {
  if (started || !isSupabaseConfigured) return false;
  started = true;
  userId = uid;

  try { await hydrate(); }  catch (err) { console.warn('[sync] hydrate failed:', err); }
  // After hydration, the workspace store has a fresh set of items. Reset the
  // automation runner's baseline so it doesn't see the entire loaded data set
  // as "just-created" and trigger the `item_created` automation for each row.
  try { resyncAutomationBaseline(); } catch {}
  try { attachRealtime(); } catch (err) { console.warn('[sync] realtime failed:', err); }
  try { attachPusher(); }   catch (err) { console.warn('[sync] pusher failed:', err); }
  return true;
}

export function stopSupabaseSync() {
  if (!started) return;
  channels.forEach((c) => c.unsubscribe());
  channels = [];
  unsubscribeStore?.();
  unsubscribeStore = null;
  userId = null;
  started = false;
}

export function isSyncing() {
  return started;
}
